import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { InventoryPartType } from '../types/inventory';

/** Client move payload — matches api-contract; server derives category/unit/location/origin/unitCost. */
export interface MovePartToInventoryEntry {
    partId: string;
    quantity: number;
    manufacturerPartNumber?: string;
    name?: string;
    partType?: InventoryPartType;
    supplierId?: string;
    restockAlert?: boolean;
    reorderQuantity?: number | null;
    targetQuantity?: number | null;
    partTerminologyId?: number;
    imageKey?: string;
    tagIds?: string[];
    binIds?: string[];
}

export interface MovePartsToInventoryInput {
    supplierOrderId: string;
    /** One part per request — client calls once per confirmation page. */
    part: MovePartToInventoryEntry;
}

export interface MovedPartResult {
    partId: string;
    movedQuantity: number;
    remainingQuantity: number;
    inventoryItemId: string;
}

export interface MovePartsToInventoryResponse {
    movedPart: MovedPartResult;
}

const movePartsToInventory = async (
    input: MovePartsToInventoryInput,
    sessionId: string | null,
): Promise<MovePartsToInventoryResponse> => {
    const { data } = await axios.post(
        `${API_URL}/order/parts/move-to-inventory`,
        input,
        {
            headers: { Authorization: sessionId },
        },
    );
    return data;
};

export const useMovePartsToInventory = (
    options?: UseMutationOptions<
        MovePartsToInventoryResponse,
        unknown,
        MovePartsToInventoryInput
    >,
) => {
    const sessionContext = useContext(SessionContext);

    return useMutation({
        mutationFn: (input: MovePartsToInventoryInput) =>
            movePartsToInventory(input, sessionContext?.sessionId ?? null),
        ...options,
    });
};
